import {BasePopup} from "components/popup/BasePopup"
import styles from "./BatchDailyAttendanceUpdates.module.css"
import {BaseButton, BaseInput, BaseLoading} from "components"
import moment from "moment"
import {useModel, useMutation} from "hooks"
import {useCallback, useEffect, useRef} from "react"
import {Checkbox} from "antd"
import {cloneDeep, uniqBy} from "lodash"
import {getFullName, handleError, toastError, toastInfo, toastSuccess, getFieldLabel} from "helpers"
import {attendanceService} from "services"
import {Settings} from "types/settings"
import {KlassappTable} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {KlassappTableProps} from "types/common"
import {useQuery} from "@tanstack/react-query"
import {UNLIMITED_PAGE_SIZE} from "data/constants"

const isEmptyAttendedMinutes = (value: string | number | null | undefined) =>
    value === null || value === "" || value === undefined

const parseAttendedMinutes = (value: string | number | null | undefined): number | null => {
    if (isEmptyAttendedMinutes(value)) {
        return null
    }

    const parsed = Number(value)
    if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
        return null
    }

    return parsed
}

const normalizeAttendedMinutesInput = (value: string | number | null | undefined) => {
    if (isEmptyAttendedMinutes(value)) {
        return ""
    }

    return String(value).replace(/[^\d]/g, "")
}

type StudentEdits = {
    attendedMinutes: string | null
    isMakeup: boolean
}

type Props = {
    isShow: boolean
    date: string
    filterParams: Record<string, any>
    onClose: () => void
    onSuccess: () => void
} & KlassappTableProps

function BatchDailyAttendanceUpdatesTable(props: Props) {
    const {
        isShow,
        onClose,
        date,
        filterParams,
        onSuccess,
        data,
        columns,
        fields,
        allFields,
        isLoading,
        isLoadedTableFuncs,
        orderField,
        dispatch,
        getCurrentData
    } = props
    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const wasOpenRef = useRef(false)
    const editsRef = useRef<Record<number, StudentEdits>>({})

    const handleFieldChange = useCallback(
        (profileId: number, key: keyof StudentEdits, value: any) => {
            const nextValue = key === "attendedMinutes" ? normalizeAttendedMinutesInput(value) : value
            const prev = editsRef.current[profileId] ?? {attendedMinutes: null, isMakeup: false}
            editsRef.current[profileId] = {
                ...prev,
                [key]: nextValue
            }

            const currentData = getCurrentData() || []
            dispatch({
                data: currentData.map((item) => {
                    if (item.profileId !== profileId) {
                        return item
                    }
                    const attendedMinutes = key === "attendedMinutes" ? nextValue : item.attendedMinutes
                    const isMakeup = key === "isMakeup" ? nextValue : item.isMakeup
                    return {
                        ...item,
                        attendedMinutes,
                        isMakeup,
                        attendedMinutesHtml: (
                            <BaseInput
                                type="number"
                                min={0}
                                value={attendedMinutes ?? ""}
                                onChange={(v) => handleFieldChange(profileId, "attendedMinutes", v)}
                                placeholder="Minutes"
                            />
                        ),
                        isMakeupHtml: (
                            <Checkbox
                                checked={!!isMakeup}
                                onChange={(event) => handleFieldChange(profileId, "isMakeup", event.target.checked)}
                            />
                        )
                    }
                })
            })
        },
        [dispatch, getCurrentData]
    )

    const normalizeItem = useCallback(
        (daily: any) => {
            const student = daily?.student ?? {}
            const shifts: any[] = uniqBy(student.shifts ?? [], "shiftId")
            const phases: any[] = uniqBy(student.phases ?? [], "phaseId")
            const profileId = student.id ?? daily.profileId
            const edits = editsRef.current[profileId] ?? {
                attendedMinutes: null,
                isMakeup: !!daily.isMakeup
            }
            if (!editsRef.current[profileId]) {
                editsRef.current[profileId] = edits
            }

            return {
                ...daily,
                id: profileId,
                profileId,
                customProfileId: student?.customProfileId ?? "",
                customUserId: student.customUserId ?? "",
                userId: student.userId,
                firstName: student.firstName ?? "",
                lastName: student.lastName ?? "",
                studentName: getFullName(student),
                phaseNames: phases.map((phase) => phase.name).join(",") ?? "",
                shiftNames: shifts.map((shift) => shift.name).join(",") ?? "",
                majorVersionHtml: student.currentMajorVersionId?.versionName
                    ? `${student.currentMajorVersionId.versionName} (${student.currentMajorVersionId.versionCode})`
                    : "",
                majorHtml: student.currentMajor?.name
                    ? `${student.currentMajor.name} (${student.currentMajor.code})`
                    : "",
                attendedMinutes: edits.attendedMinutes,
                isMakeup: edits.isMakeup,
                attendedMinutesHtml: (
                    <BaseInput
                        type="number"
                        min={0}
                        value={edits.attendedMinutes ?? ""}
                        onChange={(v) => handleFieldChange(profileId, "attendedMinutes", v)}
                        placeholder="Minutes"
                    />
                ),
                isMakeupHtml: (
                    <Checkbox
                        checked={!!edits.isMakeup}
                        onChange={(event) => handleFieldChange(profileId, "isMakeup", event.target.checked)}
                    />
                )
            }
        },
        [handleFieldChange]
    )

    const listQuery = useQuery(
        ["batch-daily-attendance-updates", isShow, date, filterParams, orderField?.field, orderField?.order],
        async () => {
            const {data: rows} = await attendanceService.getListRollCall({
                range: {
                    page: 1,
                    pageSize: UNLIMITED_PAGE_SIZE
                },
                sort: orderField?.field
                    ? {
                          orderBy: orderField.field,
                          orderDir: orderField.order
                      }
                    : undefined,
                ...filterParams
            })
            return (rows || []).map((item) => normalizeItem(item))
        },
        {
            enabled: isShow && !!isLoadedTableFuncs,
            onSuccess: (students) => {
                dispatch({data: students, total: students.length, isLoading: false})
                if (wasOpenRef.current && students.length <= 0) {
                    toastInfo("There are no students to update")
                    onClose()
                }
            },
            onError: (err) => {
                handleError(err)
                dispatch({data: [], total: 0, isLoading: false})
            }
        }
    )

    useEffect(() => {
        dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getListData", func: listQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isShow) {
            wasOpenRef.current = false
            editsRef.current = {}
            dispatch({data: [], total: 0})
            return
        }

        if (!wasOpenRef.current) {
            wasOpenRef.current = true
            editsRef.current = {}
        }
    }, [isShow, dispatch])

    function getColumns() {
        return [
            {
                title: getFieldLabel(model, Settings.GeneralLabel.EnrollmentId, "Enrollment Id"),
                field: "customProfileId",
                headerStyle: {minWidth: "100px"},
                sortable: true,
                orderField: "customProfileId"
            },
            {
                title: "Custom User Id",
                field: "customUserId",
                headerStyle: {minWidth: "100px"},
                sortable: true,
                orderField: "customUserId"
            },
            {
                title: "User Id",
                field: "userId",
                headerStyle: {minWidth: "100px"},
                sortable: true,
                orderField: "userId"
            },
            {
                title: "First Name",
                field: "firstName",
                headerStyle: {minWidth: "120px"},
                sortable: true,
                orderField: "firstName"
            },
            {
                title: "Last Name",
                field: "lastName",
                headerStyle: {minWidth: "120px"},
                sortable: true,
                orderField: "lastName"
            },
            {
                title: "Phase",
                field: "phaseNames",
                headerStyle: {minWidth: "100px"}
            },
            {
                title: "Shift",
                field: "shiftNames",
                headerStyle: {minWidth: "100px"}
            },
            {
                title: "Major",
                field: "majorHtml",
                headerStyle: {minWidth: "120px"}
            },
            {
                title: "Major Version",
                field: "majorVersionHtml",
                headerStyle: {minWidth: "120px"}
            },
            {
                title: "Attended Minutes",
                titleHtml: (
                    <span>
                        Attended Minutes<span className={styles.star}>*</span>
                    </span>
                ),
                field: "attendedMinutesHtml",
                headerStyle: {minWidth: "140px"}
            },
            {
                title: "Makeup",
                field: "isMakeupHtml",
                headerStyle: {minWidth: "80px", textAlign: "center"},
                style: {textAlign: "center"}
            }
        ]
    }

    function getFields() {
        return [
            getFieldLabel(model, Settings.GeneralLabel.EnrollmentId, "Enrollment Id"),
            "Custom User Id",
            "User Id",
            "First Name",
            "Last Name",
            "Phase",
            "Shift",
            "Major",
            "Major Version",
            "Attended Minutes",
            "Makeup"
        ]
    }

    const hasAtLeastOneAttendedMinutes = () =>
        (getCurrentData() || []).some((student) => parseAttendedMinutes(student.attendedMinutes) !== null)

    const hasInvalidAttendedMinutes = () =>
        (getCurrentData() || []).some(
            (student) =>
                !isEmptyAttendedMinutes(student.attendedMinutes) &&
                parseAttendedMinutes(student.attendedMinutes) === null
        )

    const onSubmit = () => {
        if (!hasAtLeastOneAttendedMinutes()) {
            toastError("Please input at least one student's attended minutes")
            return
        }

        if (hasInvalidAttendedMinutes()) {
            toastError("Please enter valid whole-number attended minutes")
            return
        }

        const payload = (getCurrentData() || [])
            .map((item) => ({
                item,
                attendedMinutes: parseAttendedMinutes(item.attendedMinutes)
            }))
            .filter(({attendedMinutes}) => attendedMinutes !== null)
            .map(({item, attendedMinutes}) => ({
                profileId: item.profileId,
                attendedMinutes: attendedMinutes as number,
                isMakeup: item.isMakeup ?? false,
                majorVersionId: item.student?.currentMajorVersionId?.id,
                campusId: item.student?.campuses[0].id
            }))

        batchUpdatesDailyAttendanceMutation.mutate({
            students: payload,
            date: moment(date).local().format("YYYY-MM-DD")
        })
    }

    const batchUpdatesDailyAttendanceMutation = useMutation(attendanceService.batchUpdatesDailyAttendance, {
        onSuccess: () => {
            toastSuccess("Batch daily attendance updates successfully")
            onSuccess()
        },
        onError: (error) => {
            handleError(error)
        }
    })

    return (
        <BasePopup isShow={isShow} onClose={onClose} width="90vw" leftIcon="PERSON_ADD" destroyOnClose keyboard={false}>
            <div className={styles.container}>
                <div className={styles.formTitle}>
                    Batch Daily Attendance Updates on {moment(date).format(dateFormat)}
                </div>
                <div className={styles.tableContainer}>
                    <KlassappTable
                        columns={columns}
                        data={data}
                        isLoading={isLoading || listQuery.isFetching}
                        fields={fields}
                        allFields={allFields}
                        orderField={orderField}
                        isShowScrollTable
                        onClickSortColumn={props.onClickSortColumn}
                        onChangeFields={props.onChangeFields}
                        onUpdateRowData={props.onUpdateRowData}
                        onUpdateTableData={props.onUpdateTableData}
                        onDraggableColumn={props.onDraggableColumn}
                        onChangeAllFields={props.onChangeAllFields}
                    />
                </div>
                <div className={styles.actionWrap}>
                    <BaseButton
                        title="Cancel"
                        onClick={onClose}
                        variant="secondary"
                        loading={batchUpdatesDailyAttendanceMutation.isLoading}
                    />
                    <BaseButton
                        title="Submit"
                        onClick={onSubmit}
                        disabled={(data || []).length <= 0}
                        loading={batchUpdatesDailyAttendanceMutation.isLoading}
                    />
                </div>
            </div>
            <BaseLoading isShow={batchUpdatesDailyAttendanceMutation.isLoading} />
        </BasePopup>
    )
}

export const BatchDailyAttendanceUpdates = KlassappTableHOC(BatchDailyAttendanceUpdatesTable)
